/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { Dropdown, TextInput, isEmptyValue, TextboxInput, composeValidators } from '@blend/enterprise-components';
import { FinalFormInputConfig } from 'blb-ui-types/helpers';
import { WrappedInput } from 'blb-components/WrappedInput';
import { paidByDropdownOptions, payeeDropdownOptions, paymentMilestoneDropdownOptions } from '../../constants';
import { RequestType } from '../../types';
import { Pricing } from 'blb-types/pricing';

const feeNameInputConfig: FinalFormInputConfig<TextInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { type: 'text' },
  inputComponent: TextInput,
  label: 'Fee name',
  labelSize: 'sm',
  name: 'name',
  validate: isEmptyValue('Please provide a name'),
};

const payeeInputConfig: FinalFormInputConfig<Dropdown.Props, string> = {
  component: WrappedInput,
  customComponentProps: { options: payeeDropdownOptions },
  inputComponent: Dropdown,
  label: 'Payee',
  labelSize: 'sm',
  name: 'paidTo',
  validate: isEmptyValue('Please provide a payee'),
};

const paidByInputConfig: FinalFormInputConfig<Dropdown.Props, string> = {
  component: WrappedInput,
  customComponentProps: { options: paidByDropdownOptions },
  inputComponent: Dropdown,
  label: 'Paid by',
  labelSize: 'sm',
  name: 'paidBy',
  validate: isEmptyValue('Please provide a paid by'),
};

const whenInputConfig: FinalFormInputConfig<Dropdown.Props, string> = {
  component: WrappedInput,
  customComponentProps: { options: paymentMilestoneDropdownOptions },
  inputComponent: Dropdown,
  label: 'When',
  labelSize: 'sm',
  name: 'paymentMilestone',
  validate: isEmptyValue('Please provide a payment milestone'),
};

const getFeeCalculationTypeInputConfig = (
  subsectionKey: Pricing.LoanEstimateSectionTypes,
): FinalFormInputConfig<Dropdown.Props, string> => {
  let options;

  switch (subsectionKey) {
    case Pricing.LoanEstimateSectionTypes.OriginationCharges:
      options = [
        { text: 'Percentage', value: 'Percentage' },
        { text: 'Fixed', value: 'Fixed' },
      ];
      break;
    case Pricing.LoanEstimateSectionTypes.Prepaids:
      options = [
        { text: 'Time Months', value: 'TimeMonths' },
        { text: 'Time Days', value: 'TimeDays' },
      ];
      break;
    case Pricing.LoanEstimateSectionTypes.Escrow:
      options = [{ text: 'Time Months', value: 'TimeMonths' }];
      break;
    default:
      options = [{ text: 'Fixed', value: 'Fixed' }];
  }
  return {
    component: WrappedInput,
    customComponentProps: { options },
    inputComponent: Dropdown,
    label: 'Fee type',
    labelSize: 'sm',
    name: 'dataType',
    validate: isEmptyValue('Please provide a fee type'),
  };
};

const amountByInputConfig: FinalFormInputConfig<TextInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { type: 'moneyCents' },
  inputComponent: TextInput,
  label: 'Amount',
  labelSize: 'sm',
  name: 'amount',
  validate: isEmptyValue('Please provide an amount'),
};

const totalAmountInputConfig: FinalFormInputConfig<TextInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { type: 'moneyCents' },
  inputComponent: TextInput,
  label: 'Total amount',
  labelSize: 'sm',
  name: 'totalAmount',
  disabled: true,
};

const percentageAmountInputConfig: FinalFormInputConfig<TextInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { type: 'percentage' },
  inputComponent: TextInput,
  label: 'Percentage amount',
  labelSize: 'sm',
  name: 'percentageAmount',
  validate: isEmptyValue('Please provide a percentage amount'),
};

const baseValueForPercentageInputConfig: FinalFormInputConfig<TextInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { type: 'moneyCents' },
  inputComponent: TextInput,
  label: 'Base value for percentage',
  labelSize: 'sm',
  name: 'baseValueForPercentage',
  validate: isEmptyValue('Please provide a base value'),
};

const numberOfTimePeriodsInputConfig: FinalFormInputConfig<TextInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { type: 'number' },
  inputComponent: TextInput,
  label: 'Number of periods',
  labelSize: 'sm',
  name: 'numberOfTimePeriods',
  validate: isEmptyValue('Please provide a number of time periods'),
};

const amountPerTimePeriodInputConfig: FinalFormInputConfig<TextInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { type: 'moneyCents' },
  inputComponent: TextInput,
  label: 'Amount per time period',
  labelSize: 'sm',
  name: 'amountPerTimePeriod',
  validate: isEmptyValue('Please provide an amount per time period'),
};

const messageInputConfig: FinalFormInputConfig<TextboxInput.Props, string> = {
  component: WrappedInput,
  customComponentProps: { rows: 8, type: 'text' },
  inputComponent: TextboxInput,
  label: 'Message',
  labelSize: 'sm',
  name: 'message',
  placeholder: 'Add a message about this request',
  validate: composeValidators(isEmptyValue('Please provide a message body'), (value: string) => {
    return !!value && value.length > 1024 * 3 ? 'Please keep the message less than 3072 characters' : undefined;
  }),
};

const COLUMN_CONFIG_DEFAULT = [payeeInputConfig, paidByInputConfig, whenInputConfig];
const COLUMN_CONFIG_NO_PAYEE = [paidByInputConfig, whenInputConfig];

const getAdditionalRequestFields = (subsectionKey: Pricing.LoanEstimateSectionTypes) => {
  switch (subsectionKey) {
    case Pricing.LoanEstimateSectionTypes.Payoffs:
      return [];
    case Pricing.LoanEstimateSectionTypes.LenderCredits:
    case Pricing.LoanEstimateSectionTypes.Escrow:
      return COLUMN_CONFIG_NO_PAYEE;
    default:
      return COLUMN_CONFIG_DEFAULT;
  }
};

const getFieldsBasedOnFeeType = (dataType: Pricing.DataType) => {
  switch (dataType) {
    case 'Percentage':
      return [baseValueForPercentageInputConfig, percentageAmountInputConfig, totalAmountInputConfig];
    case 'TimeMonths':
    case 'TimeDays':
      return [numberOfTimePeriodsInputConfig, amountPerTimePeriodInputConfig, totalAmountInputConfig];
    case 'Fixed':
      return [amountByInputConfig];
    default:
      return [];
  }
};

export const getRequestFields = (
  subsectionKey: Pricing.LoanEstimateSectionTypes,
  dataType: Pricing.DataType,
): Record<RequestType, FinalFormInputConfig<TextInput.Props | Dropdown.Props | TextboxInput.Props, string>[]> => {
  return {
    add: [
      feeNameInputConfig,
      ...getAdditionalRequestFields(subsectionKey),
      getFeeCalculationTypeInputConfig(subsectionKey),
      ...getFieldsBasedOnFeeType(dataType),
      { ...messageInputConfig, ...{ placeholder: 'Add an optional message about the fee', validate: undefined } },
    ],
    delete: [messageInputConfig],
    edit: [
      feeNameInputConfig,
      ...getAdditionalRequestFields(subsectionKey),
      getFeeCalculationTypeInputConfig(subsectionKey),
      ...getFieldsBasedOnFeeType(dataType),
      messageInputConfig,
    ],
  };
};
